import React from "react";

// Minimal markdown renderer for admin-authored copy (announcements). Supports
// exactly the subset the admin UI advertises: paragraphs (blank-line
// separated), "- " bullet lists, **bold**, *italic* / _italic_ and
// [links](url). Everything is built as React nodes - no dangerouslySetInnerHTML
// - and link hrefs are restricted to https or app-relative paths, so a stray
// javascript: URL renders as plain text. Plain text is valid input and renders
// as ordinary paragraphs, which keeps pre-rich-text announcements intact.

// Bold is non-greedy (.+?) so it can contain nested *italic*; single-star
// italic stays [^*]+ so it never swallows a ** delimiter.
const INLINE_RE = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*(.+?)\*\*|\*([^*]+)\*|_([^_]+)_/g;

function isSafeHref(url: string): boolean {
  return url.startsWith("https://") || (url.startsWith("/") && !url.startsWith("//"));
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) nodes.push(text.slice(last, idx));
    const key = `${keyPrefix}-${i++}`;
    const [, linkText, linkUrl, bold, star, underscore] = m;
    if (linkText !== undefined && linkUrl !== undefined) {
      if (isSafeHref(linkUrl)) {
        const external = linkUrl.startsWith("https://");
        nodes.push(
          <a
            key={key}
            href={linkUrl}
            {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            style={{ color: "var(--text-accent)", fontWeight: 600 }}
          >
            {renderInline(linkText, key)}
          </a>,
        );
      } else {
        nodes.push(linkText);
      }
    } else if (bold !== undefined) {
      nodes.push(
        <strong key={key} style={{ fontWeight: 700, color: "var(--text-primary)" }}>
          {renderInline(bold, key)}
        </strong>,
      );
    } else {
      nodes.push(<em key={key}>{renderInline(star ?? underscore ?? "", key)}</em>);
    }
    last = idx + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function Markdown({ text }: { text: string }) {
  const blocks = text
    .replaceAll("\r\n", "\n")
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);

  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split("\n");
        if (lines.every((l) => l.trimStart().startsWith("- "))) {
          return (
            <ul key={bi} style={{ margin: "0 0 10px", paddingLeft: 20 }}>
              {lines.map((l, li) => (
                <li key={li} style={{ marginBottom: 2 }}>
                  {renderInline(l.trimStart().slice(2), `${bi}-${li}`)}
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={bi} style={{ margin: "0 0 10px" }}>
            {lines.map((l, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {renderInline(l, `${bi}-${li}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}
